'use client';

import { useCallback, useEffect, useState } from 'react';

import Link from 'next/link';

import { ArrowRightIcon, X } from 'lucide-react';

import { usePathname } from '@kit/i18n/navigation';

/**
 * Checks if the current path is a vertical-specific landing page.
 * These pages have their own conversion flow and should not show the
 * generic "free pipeline audit" exit-intent popup.
 *
 * Uses next-intl's usePathname which always strips the locale prefix,
 * so segments are [vertical, city] regardless of locale.
 */
function isVerticalLandingPage(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);

  // segments[0] = vertical, segments[1] = city (locale already stripped by next-intl)
  // Vertical landing pages have 2+ segments: /drivers/johannesburg
  // Marketing pages either have 1 segment (/pricing) or their prefix is excluded
  const marketingPrefixes = [
    'admin',
    'auth',
    'blog',
    'case-studies',
    'changelog',
    'compare',
    'contact',
    'cookie-policy',
    'datenschutz',
    'docs',
    'faq',
    'home',
    'pricing',
    'privacy-policy',
    'referrals',
    'resources',
    'terms-of-service',
    'verticals',
  ];

  return segments.length >= 2 && !marketingPrefixes.includes(segments[0] ?? '');
}

export function ExitIntentPopup() {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const pathname = usePathname();

  const handleMouseLeave = useCallback(
    (e: MouseEvent) => {
      if (e.clientY <= 0 && !dismissed) {
        setShow(true);
      }
    },
    [dismissed],
  );

  useEffect(() => {
    // Don't show on vertical landing pages (they have their own CTAs)
    if (isVerticalLandingPage(pathname)) return;

    // Only show on desktop, after 5 seconds on page
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 5000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseLeave, pathname]);

  const dismiss = () => {
    setShow(false);
    setDismissed(true);
  };

  if (!show) return null;

  return (
    <div className="site-exit-intent fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={dismiss}
      />

      {/* Popup */}
      <div className="bg-card relative z-10 w-full max-w-md rounded-2xl border p-8 shadow-2xl">
        <button
          onClick={dismiss}
          className="text-muted-foreground hover:text-foreground absolute top-4 right-4 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="text-center">
          <div className="bg-primary/10 text-primary mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl">
            <ArrowRightIcon className="h-6 w-6" />
          </div>

          <h3 className="text-xl font-semibold tracking-tight">
            Get a free pipeline audit
          </h3>

          <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
            See exactly how many qualified leads we can deliver for your
            business. We respond within 1 business day.
          </p>

          <div className="bg-muted/50 mx-auto mt-4 flex max-w-xs items-center justify-center gap-4 rounded-lg p-3">
            <div className="text-center">
              <p className="text-primary text-lg font-bold">$0</p>
              <p className="text-muted-foreground text-xs">Setup cost</p>
            </div>
            <div className="bg-border h-8 w-px" />
            <div className="text-center">
              <p className="text-primary text-lg font-bold">48h</p>
              <p className="text-muted-foreground text-xs">To first leads</p>
            </div>
            <div className="bg-border h-8 w-px" />
            <div className="text-center">
              <p className="text-primary text-lg font-bold">0</p>
              <p className="text-muted-foreground text-xs">Lock-in</p>
            </div>
          </div>

          <Link
            href="/contact"
            onClick={dismiss}
            className="bg-primary text-primary-foreground mt-5 inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-medium transition-colors hover:opacity-90"
          >
            Get Your Free Audit
            <ArrowRightIcon className="h-4 w-4" />
          </Link>

          <button
            onClick={dismiss}
            className="text-muted-foreground hover:text-foreground mt-3 block w-full text-center text-xs transition-colors"
          >
            No thanks, I have enough leads
          </button>
        </div>
      </div>
    </div>
  );
}
